import {
  LEAD_SOURCE_CALENDAR,
  MeetingPreparationErrorRetryability,
  NODE_DATA_CALENDAR_DISMISSED_KEY,
  type GraphNode,
  type MeetingPreparationErrorCode,
  type MeetingPreparationGap,
  type MeetingPreparationParticipant,
  type MeetingPreparationRequest,
  type MeetingPreparationResponse,
  type MeetingPreparationSource
} from "@salesko/contracts";
import { AlertCircle, Loader2, NotebookPen, RefreshCw } from "lucide-react";
import type { CSSProperties } from "react";
import { useI18n, type Dictionary } from "../../i18n";
import { entitySingularLabel } from "../i18n";
import { compactDateTime, shortChangeSetId } from "../lib/format";

export type CanPrepareMeetingInput = {
  hasSession: boolean;
  node: GraphNode | null;
  readOnly: boolean;
};

/**
 * Visibility gate for the Prepare command and panel. Reads the same node.data
 * fields DetailPanel's dismiss button already depends on (leadSource, provider,
 * providerEventId, the calendarDismissed flag — DetailPanel.tsx:248-257) but is
 * intentionally stricter: it also requires hasSession and !readOnly, which the
 * pre-existing dismiss gating does not check. This is a deliberate, independent
 * read of node.data rather than a shared helper with DetailPanel's local consts,
 * so this new gate can never perturb dismiss's existing behavior.
 */
export function canPrepareMeeting({ hasSession, node, readOnly }: CanPrepareMeetingInput): boolean {
  if (!node || !hasSession || readOnly) {
    return false;
  }
  if (node.type !== "event" || node.data.leadSource !== LEAD_SOURCE_CALENDAR) {
    return false;
  }
  if (node.data[NODE_DATA_CALENDAR_DISMISSED_KEY] === true) {
    return false;
  }
  const providerEventId = typeof node.data.providerEventId === "string" ? node.data.providerEventId : null;
  if (!providerEventId) {
    return false;
  }
  const provider = node.data.calendarProvider;
  return provider === "google" || provider === "microsoft";
}

export type MeetingPreparationErrorCategory = "entitlement" | "generic" | "model-error" | "not-eligible" | "stale-event";

/**
 * Exhaustive over MeetingPreparationErrorCode (packages/contracts/src/meeting-preparation.ts).
 * No default/catch-all arm on purpose: adding a new code to that union without adding a
 * matching case here fails `apps/web typecheck` (TS2366, no implicit return) instead of
 * silently falling into one of these five buckets.
 */
export function meetingPreparationErrorCategory(code: MeetingPreparationErrorCode): MeetingPreparationErrorCategory {
  switch (code) {
    case "meeting_preparation_entitlement_required":
      return "entitlement";
    case "meeting_preparation_event_not_found":
    case "meeting_preparation_event_not_eligible":
      return "not-eligible";
    case "meeting_preparation_event_stale":
      return "stale-event";
    case "meeting_preparation_model_unavailable":
    case "meeting_preparation_model_timeout":
    case "meeting_preparation_invalid_model_output":
      return "model-error";
    case "meeting_preparation_request_invalid":
    case "meeting_preparation_auth_required":
    case "meeting_preparation_graph_read_failed":
    case "meeting_preparation_graph_context_invalid":
    case "meeting_preparation_unsupported_source":
      return "generic";
  }
}

/**
 * `code: null` covers a mutation failure that did not carry a stable
 * `meeting_preparation_*` code — a transport-level failure (fetch TypeError,
 * a Cloudflare edge timeout/524 returning HTML, a success-body schema drift
 * producing a ZodError) rather than a documented API error. It renders the
 * same generic, cause-agnostic copy and stays retryable; it does not
 * fabricate a stable code the API never sent.
 */
export type MeetingPreparationPanelState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; code: MeetingPreparationErrorCode | null }
  | { status: "success"; response: MeetingPreparationResponse };

export type MeetingPreparationPanelProps = {
  onPrepare: () => void;
  onRetry: () => void;
  state: MeetingPreparationPanelState;
};

/**
 * A `useMutation` snapshot reduced to just the fields `panelStateFor` needs.
 * `variables` is carried on every non-idle status because TanStack Query keeps
 * the last `.mutate()` argument around for the lifetime of that mutation
 * attempt (pending/success/error) — this is what lets `panelStateFor` tell a
 * same-node result from a stale one. `error` is already reduced to
 * `code: MeetingPreparationErrorCode | null` by the caller (DetailPanel does
 * the `instanceof MeetingPreparationApiError` check), so this file does not
 * need to import anything from `lib/api.ts`.
 */
export type MeetingPreparationMutationSnapshot =
  | { status: "idle" }
  | { status: "pending"; variables: MeetingPreparationRequest }
  | { status: "success"; data: MeetingPreparationResponse; variables: MeetingPreparationRequest }
  | { status: "error"; code: MeetingPreparationErrorCode | null; variables: MeetingPreparationRequest };

/**
 * Projects a mutation snapshot onto MeetingPreparationPanelState, gated on the
 * mutation's `variables` matching the node currently being viewed
 * (`currentNodeKey`, DetailPanel's `{provider, providerEventId}` for the
 * selected node, or null when Prepare isn't valid for it).
 *
 * Without this gate there is a real stale-result window: DetailPanel resets
 * the mutation object in a `useEffect` keyed on `node?.id` (necessarily a
 * *passive* effect — it runs after commit), but the panel's props are computed
 * during render, which happens first. Switching from an event A whose brief
 * already succeeded to a different event B would otherwise render B's very
 * first frame with A's still-cached success data, for one frame, before the
 * effect fires and clears it. Comparing `variables` against `currentNodeKey`
 * makes that first render correct synchronously; the reset effect still runs
 * afterward so a later re-prepare on B starts from a clean mutation object
 * rather than replaying A's cached result.
 */
export function panelStateFor(
  mutation: MeetingPreparationMutationSnapshot,
  currentNodeKey: MeetingPreparationRequest | null
): MeetingPreparationPanelState {
  if (mutation.status === "idle" || !requestKeyMatches(mutation.variables, currentNodeKey)) {
    return { status: "idle" };
  }
  if (mutation.status === "pending") {
    return { status: "loading" };
  }
  if (mutation.status === "success") {
    return { status: "success", response: mutation.data };
  }
  return { status: "error", code: mutation.code };
}

function requestKeyMatches(variables: MeetingPreparationRequest, currentNodeKey: MeetingPreparationRequest | null): boolean {
  return (
    currentNodeKey !== null &&
    variables.provider === currentNodeKey.provider &&
    variables.providerEventId === currentNodeKey.providerEventId
  );
}

const resultStackStyle: CSSProperties = {
  display: "grid",
  gap: "12px"
};

const resultHeadingStyle: CSSProperties = {
  margin: 0,
  color: "var(--text-strong)",
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-body-md)",
  fontWeight: "var(--fw-bold)"
};

const groupStyle: CSSProperties = {
  display: "grid",
  gap: "8px"
};

const itemStackStyle: CSSProperties = {
  display: "grid",
  gap: "3px"
};

// minWidth: 0 + overflowWrap: "anywhere": these are grid items (groupStyle/
// itemStackStyle children) of unbounded, model/user-sourced length — an email,
// a provenance id, or generated prose. Grid items default to min-width: auto
// (sized to their content's min-content), which lets an unbroken long string
// push the whole detail panel wider instead of wrapping; overflowWrap alone
// doesn't fix that without minWidth: 0 to let the item shrink first.
const proseStyle: CSSProperties = {
  margin: 0,
  minWidth: 0,
  overflowWrap: "anywhere",
  color: "var(--text-body)",
  fontSize: "var(--text-body-md)",
  lineHeight: "var(--leading-normal)"
};

const mutedStyle: CSSProperties = {
  margin: 0,
  minWidth: 0,
  overflowWrap: "anywhere",
  color: "var(--text-muted)",
  fontSize: "var(--text-body-sm)"
};

const orderedListStyle: CSSProperties = {
  margin: 0,
  minWidth: 0,
  overflowWrap: "anywhere",
  paddingLeft: "20px",
  color: "var(--text-body)",
  fontSize: "var(--text-body-md)",
  lineHeight: "var(--leading-normal)"
};

const orderedListItemStyle: CSSProperties = {
  marginBottom: "6px"
};

const truncationStyle: CSSProperties = {
  margin: 0,
  color: "var(--warning-text, var(--text-secondary))",
  fontSize: "var(--text-caption)"
};

export function MeetingPreparationPanel({ onPrepare, onRetry, state }: MeetingPreparationPanelProps) {
  const { t } = useI18n();

  return (
    <section aria-label={t.meetingPreparation.ariaLabel} className="detail-section">
      <div className="section-heading-row">
        <p className="eyebrow">{t.meetingPreparation.title}</p>
      </div>
      {state.status === "idle" ? renderIdle(t, onPrepare) : null}
      {state.status === "loading" ? renderLoading(t) : null}
      {state.status === "error" ? renderError(state.code, t, onRetry) : null}
      {state.status === "success" ? renderSuccess(state.response, t) : null}
    </section>
  );
}

function renderIdle(t: Dictionary, onPrepare: () => void) {
  return (
    <>
      <p className="empty-line">{t.meetingPreparation.idleBody}</p>
      <button
        aria-label={t.meetingPreparation.prepareActionTitle}
        className="secondary-button"
        onClick={onPrepare}
        title={t.meetingPreparation.prepareActionTitle}
        type="button"
      >
        <NotebookPen size={15} />
        <span>{t.meetingPreparation.prepareAction}</span>
      </button>
    </>
  );
}

function renderLoading(t: Dictionary) {
  return (
    <p className="history-loading">
      <Loader2 className="spin" size={14} />
      {t.meetingPreparation.loading}
    </p>
  );
}

// code is null for a mutation failure with no stable meeting_preparation_* code
// (transport failure: fetch TypeError, a Cloudflare edge timeout/524 returning
// HTML instead of JSON, a success-body schema drift throwing a ZodError — see
// MeetingPreparationPanelState). That case renders the same generic,
// cause-agnostic copy as an unmapped API code and stays retryable — silently
// falling back to idle would give the user zero feedback after a real failure.
function renderError(code: MeetingPreparationErrorCode | null, t: Dictionary, onRetry: () => void) {
  const message = code === null ? t.meetingPreparation.errorGeneric : meetingPreparationErrorMessage(meetingPreparationErrorCategory(code), t);
  const retryable = code === null ? true : MeetingPreparationErrorRetryability[code];
  return (
    <>
      <p className="history-error" role="alert">
        <AlertCircle size={14} />
        {message}
      </p>
      {retryable ? (
        <button className="secondary-button" onClick={onRetry} type="button">
          <RefreshCw size={15} />
          <span>{t.common.retry}</span>
        </button>
      ) : null}
    </>
  );
}

function meetingPreparationErrorMessage(category: MeetingPreparationErrorCategory, t: Dictionary): string {
  switch (category) {
    case "entitlement":
      return t.meetingPreparation.errorEntitlement;
    case "not-eligible":
      return t.meetingPreparation.errorNotEligible;
    case "stale-event":
      return t.meetingPreparation.errorStaleEvent;
    case "model-error":
      return t.meetingPreparation.errorModelError;
    case "generic":
      return t.meetingPreparation.errorGeneric;
  }
}

function renderSuccess(response: MeetingPreparationResponse, t: Dictionary) {
  const { brief, sources, gaps, bounds, participants } = response;

  return (
    <div style={resultStackStyle}>
      <h3 style={resultHeadingStyle} tabIndex={-1}>
        {t.meetingPreparation.successHeading}
      </h3>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.goalTitle}</p>
        <p style={proseStyle}>{brief.goal.text}</p>
      </div>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.participantsTitle}</p>
        <div style={groupStyle}>
          {participants.map((participant) => (
            <div key={participant.calendarSourceId} style={itemStackStyle}>
              <p style={proseStyle}>{participant.name ?? participant.email}</p>
              {participant.name ? <p style={mutedStyle}>{participant.email}</p> : null}
              {participantContextFor(brief.participantContext, participant).map((item, index) => (
                <p key={index} style={mutedStyle}>
                  {item.text}
                </p>
              ))}
            </div>
          ))}
        </div>
        {brief.knownContext.length > 0 ? (
          <div style={groupStyle}>
            {brief.knownContext.map((item, index) => (
              <p key={index} style={proseStyle}>
                {item.text}
              </p>
            ))}
          </div>
        ) : (
          <p className="empty-line">{t.meetingPreparation.knownContextEmpty}</p>
        )}
      </div>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.relationshipSignalsTitle}</p>
        {brief.relationshipSignals.length > 0 ? (
          <div style={groupStyle}>
            {brief.relationshipSignals.map((item, index) => (
              <p key={index} style={proseStyle}>
                {item.text}
              </p>
            ))}
          </div>
        ) : (
          <p className="empty-line">{t.meetingPreparation.relationshipSignalsEmpty}</p>
        )}
      </div>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.watchoutsTitle}</p>
        {brief.watchouts.length > 0 ? (
          <div style={groupStyle}>
            {brief.watchouts.map((item, index) => (
              <p key={index} style={proseStyle}>
                {item.text}
              </p>
            ))}
          </div>
        ) : (
          <p className="empty-line">{t.meetingPreparation.watchoutsEmpty}</p>
        )}
      </div>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.suggestedQuestionsTitle}</p>
        <ol style={orderedListStyle}>
          {brief.suggestedQuestions.map((item, index) => (
            <li key={index} style={orderedListItemStyle}>
              {item.text}
            </li>
          ))}
        </ol>
      </div>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.suggestedCloseTitle}</p>
        <p style={proseStyle}>{brief.suggestedClose.text}</p>
      </div>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.sourcesTitle}</p>
        <div style={groupStyle}>
          {sources.map((source) => (
            <div key={source.sourceId} style={itemStackStyle}>
              <p style={proseStyle}>{sourceKindLabel(source, t)}</p>
              <p style={mutedStyle}>{sourceDetailLine(source, t)}</p>
            </div>
          ))}
        </div>
        {bounds.totalSources.truncated ? <p style={truncationStyle}>{t.meetingPreparation.sourcesTruncated}</p> : null}
      </div>

      <div style={groupStyle}>
        <p className="eyebrow">{t.meetingPreparation.gapsTitle}</p>
        {gaps.length > 0 ? (
          <div style={groupStyle}>
            {gaps.map((gap, index) => (
              <p key={index} style={proseStyle}>
                {gapLabel(gap, participants, t)}
              </p>
            ))}
          </div>
        ) : (
          <p className="empty-line">{t.meetingPreparation.gapsEmpty}</p>
        )}
      </div>
    </div>
  );
}

function participantContextFor(
  participantContext: MeetingPreparationResponse["brief"]["participantContext"],
  participant: MeetingPreparationParticipant
) {
  return participantContext.filter((item) => item.participantSourceId === participant.calendarSourceId);
}

// Exhaustive over MeetingPreparationSource["kind"] (6 members) — no default arm.
function sourceKindLabel(source: MeetingPreparationSource, t: Dictionary): string {
  switch (source.kind) {
    case "calendar_event":
      return t.meetingPreparation.sourceCalendarEvent;
    case "calendar_attendee":
      return t.meetingPreparation.sourceCalendarAttendee;
    case "graph_node":
      return entitySingularLabel(t, source.entityType);
    case "graph_edge":
      return t.meetingPreparation.sourceGraphEdge;
    case "opportunity":
      return t.entities.opportunitySingular;
    case "evidence":
      return t.meetingPreparation.sourceEvidence;
  }
}

// Exhaustive over MeetingPreparationSource["kind"] (6 members) — no default arm.
function sourceDetailLine(source: MeetingPreparationSource, t: Dictionary): string {
  switch (source.kind) {
    case "calendar_event":
      return source.provider;
    case "calendar_attendee":
      return source.attendeeEmail;
    case "graph_node":
    case "graph_edge":
    case "opportunity":
    case "evidence":
      return t.meetingPreparation.sourceFrameProvenance(shortChangeSetId(source.frameRecordId), compactDateTime(source.acceptedAt));
  }
}

// Exhaustive over MeetingPreparationGap["code"] (5 members) — no default arm.
function gapLabel(gap: MeetingPreparationGap, participants: MeetingPreparationParticipant[], t: Dictionary): string {
  switch (gap.code) {
    case "no_graph_person_match": {
      const participant = participants.find((candidate) => candidate.calendarSourceId === gap.participantSourceId);
      return t.meetingPreparation.gapNoGraphPersonMatch(participant?.name ?? participant?.email ?? gap.participantSourceId);
    }
    case "no_relationship_evidence":
      return t.meetingPreparation.gapNoRelationshipEvidence;
    case "no_opportunity_evidence":
      return t.meetingPreparation.gapNoOpportunityEvidence;
    case "context_truncated":
      return t.meetingPreparation.gapContextTruncated(
        gap.collections.map((collection) => t.meetingPreparation.truncatedCollectionLabels[collection]).join(", ")
      );
    case "optional_calendar_detail_unavailable":
      return t.meetingPreparation.gapOptionalCalendarDetailUnavailable(calendarDetailGapLabel(gap.detail, t));
  }
}

// Exhaustive over the four "optional_calendar_detail_unavailable" detail values — no default arm.
function calendarDetailGapLabel(detail: "end_at" | "location" | "organizer" | "summary", t: Dictionary): string {
  switch (detail) {
    case "summary":
      return t.meetingPreparation.calendarDetailLabels.summary;
    case "location":
      return t.meetingPreparation.calendarDetailLabels.location;
    case "end_at":
      return t.meetingPreparation.calendarDetailLabels.endAt;
    case "organizer":
      return t.meetingPreparation.calendarDetailLabels.organizer;
  }
}
